// Google Maps loader utility with new Places API
declare global {
  interface Window {
    _gmapsLoaded?: boolean;
    _gmapsLoading?: Promise<void>;
    google?: any;
    initMap?: () => void;
  }
}

export async function loadGoogleMapsScript(apiKey: string): Promise<void> {
  // Return if already loaded and working
  if (window._gmapsLoaded && window.google?.maps?.places) {
    console.log('Google Maps already loaded, reusing');
    return Promise.resolve();
  }

  // Return existing loading promise if in progress
  if (window._gmapsLoading) {
    console.log('Google Maps loading in progress, waiting...');
    return window._gmapsLoading;
  }

  // Check if script already exists and is working
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript && window.google?.maps) {
    console.log('Google Maps script exists and maps API is available');
    // Wait a bit for places library to be ready
    window._gmapsLoading = new Promise<void>((resolve) => {
      const checkPlaces = () => {
        if (window.google?.maps?.places) {
          console.log('Google Maps Places library confirmed ready');
          window._gmapsLoaded = true;
          window._gmapsLoading = undefined;
          resolve();
        } else {
          setTimeout(checkPlaces, 50);
        }
      };
      checkPlaces();
    });
    return window._gmapsLoading;
  }

  // Create new loading promise
  window._gmapsLoading = new Promise<void>((resolve, reject) => {
    // Remove ALL existing Google Maps scripts to prevent conflicts
    const allScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    allScripts.forEach(script => {
      console.log('Removing existing Google Maps script:', script.getAttribute('src'));
      script.remove();
    });

    // Set up callback
    window.initMap = async () => {
      console.log('initMap callback triggered');
      try {
        // Explicitly import the Places library using the modern API to ensure Places (New)
        if (window.google?.maps?.importLibrary) {
          await (window.google.maps as any).importLibrary('places');
        }
      } catch (e) {
        console.warn('importLibrary("places") failed or unavailable, continuing with global namespace', e);
      }
      // Wait a bit for Places library to fully initialize
      setTimeout(() => {
        if (window.google?.maps?.places) {
          console.log('Google Maps fully loaded with Places library');
          window._gmapsLoaded = true;
          resolve();
        } else {
          console.error('Google Maps loaded but Places library not available');
          window._gmapsLoading = undefined;
          window._gmapsLoaded = false;
          reject(new Error('Places library not available'));
        }
      }, 100);
    };

    const script = document.createElement('script');
    // Load with Places API (New) - using v=beta for new features
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=beta&callback=initMap`;
    script.async = true;
    script.defer = true;
    
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
      window._gmapsLoading = undefined;
      window._gmapsLoaded = false;
      reject(new Error('Failed to load Google Maps'));
    };
    
    console.log('Appending Google Maps script to document');
    document.head.appendChild(script);
  });

  return window._gmapsLoading;
}

// Cleanup utility for Google Places elements
export function cleanupGooglePlacesElements(): void {
  try {
    // Clean up all autocomplete host containers
    const hosts = document.querySelectorAll('[data-gmp-autocomplete-host]');
    hosts.forEach(host => {
      try {
        while (host.firstChild) {
          if (host.contains(host.firstChild)) {
            host.removeChild(host.firstChild);
          } else {
            break;
          }
        }
      } catch (error) {
        console.warn('Host cleanup warning (safe to ignore):', error);
      }
    });

    // Clean up any orphaned Google Places elements
    const placesElements = document.querySelectorAll('gmp-place-autocomplete');
    placesElements.forEach(element => {
      try {
        if (element.parentNode && element.parentNode.contains(element)) {
          element.parentNode.removeChild(element);
        }
      } catch (error) {
        console.warn('Places element cleanup warning (safe to ignore):', error);
      }
    });
  } catch (error) {
    console.warn('Global Places cleanup warning (safe to ignore):', error);
  }
}

// Helper to create autocomplete using new Places API
export async function createPlaceAutocompleteElement(
  inputElement: HTMLInputElement,
  onPlaceSelect: (place: any) => void
): Promise<any> {
  // Ensure Google Maps is loaded
  if (!window.google?.maps?.places) {
    console.error('Google Maps Places library not loaded. Available:', {
      google: !!window.google,
      maps: !!window.google?.maps,
      places: !!window.google?.maps?.places
    });
    throw new Error('Google Maps Places library not loaded');
  }

  try {
    console.log('Creating Autocomplete for input:', inputElement);
    
    // Preferred: New Places (New) Autocomplete Element
    if (window.google.maps.places.PlaceAutocompleteElement) {
      console.log('Using new PlaceAutocompleteElement API');

      const autocompleteElement = new window.google.maps.places.PlaceAutocompleteElement();
      
      // Add cleanup method to prevent DOM errors
      (autocompleteElement as any)._cleanup = () => {
        try {
          if (autocompleteElement.parentNode && autocompleteElement.parentNode.contains(autocompleteElement)) {
            autocompleteElement.parentNode.removeChild(autocompleteElement);
          }
        } catch (error) {
          console.warn('Autocomplete cleanup warning (safe to ignore):', error);
        }
      };

      // Always append the element and retire the original input to avoid read-only binding issues
      try {
        inputElement.required = false;
        // Fully hide original input so it doesn't show as a duplicate
        inputElement.setAttribute('aria-hidden', 'true');
        inputElement.setAttribute('tabindex', '-1');
        inputElement.style.display = 'none';
      } catch {}
      
      // Prefer a dedicated host container to avoid React reconciliation issues
      let host: Element | null = null;
      try {
        const parent = inputElement.parentElement;
        host = parent?.querySelector('[data-gmp-autocomplete-host]') || null;
        if (!host) {
          // Try looking up the tree for a declared container
          const container = inputElement.closest('[data-gmp-autocomplete-container]');
          host = container?.querySelector('[data-gmp-autocomplete-host]') || null;
        }
      } catch {}
      
      if (host) {
        // Clear existing content safely before adding new element
        try {
          while (host.firstChild) {
            if (host.contains(host.firstChild)) {
              host.removeChild(host.firstChild);
            } else {
              break;
            }
          }
        } catch {}
        
        // Safely append new element
        try {
          host.appendChild(autocompleteElement);
        } catch (error) {
          console.warn('Error appending autocomplete to host:', error);
        }
      } else {
        // Construct a stable host next to the input to avoid body positioning
        const autoHost = document.createElement('div');
        autoHost.setAttribute('data-gmp-autocomplete-host', '');
        autoHost.style.width = '100%';
        // Match the shadcn Input outline the original <input> would have rendered,
        // so the autocomplete reads as a real input control rather than borderless text.
        // Do NOT set overflow:hidden here — the gmp-place-autocomplete suggestions
        // dropdown renders as a child popup and gets clipped by the host overflow.
        autoHost.style.border = '1px solid hsl(var(--input))';
        autoHost.style.borderRadius = '0.375rem';
        try {
          inputElement.insertAdjacentElement('afterend', autoHost);
          autoHost.appendChild(autocompleteElement);
        } catch {
          try {
            (inputElement.parentElement || document.body).appendChild(autoHost);
            autoHost.appendChild(autocompleteElement);
          } catch (error) {
            console.warn('Error creating autocomplete host:', error);
          }
        }
      }

      // Apply theme-aware styling to match the current dark/light mode
      try {
        const s = (autocompleteElement as any).style;
        autocompleteElement.style.display = 'block';
        autocompleteElement.style.width = '100%';

        const isDark = document.documentElement.classList.contains('dark');
        const surface = isDark ? '#1c1917' : '#ffffff';
        const surfaceContainer = isDark ? '#292524' : '#f9fafb';
        const surfaceContainerHigh = isDark ? '#44403c' : '#f3f4f6';
        const onSurface = isDark ? '#e7e5e4' : '#1f2937';
        const onSurfaceVariant = isDark ? '#a8a29e' : '#374151';
        const outline = isDark ? '#57534e' : '#d1d5db';
        const outlineVariant = isDark ? '#44403c' : '#e5e7eb';

        // Material Design tokens
        s.setProperty('--md-sys-color-surface', surface);
        s.setProperty('--md-sys-color-surface-container', surfaceContainer);
        s.setProperty('--md-sys-color-surface-container-high', surfaceContainerHigh);
        s.setProperty('--md-sys-color-surface-container-highest', surface);
        s.setProperty('--md-sys-color-on-surface', onSurface);
        s.setProperty('--md-sys-color-on-surface-variant', onSurfaceVariant);
        s.setProperty('--md-sys-color-outline', outline);
        s.setProperty('--md-sys-color-outline-variant', outlineVariant);
        s.setProperty('--md-sys-color-primary', '#a855f7');
        s.setProperty('--md-sys-color-on-primary', '#ffffff');
        s.setProperty('--md-sys-color-primary-container', isDark ? '#581c87' : '#f3e8ff');
        s.setProperty('--md-sys-color-on-primary-container', isDark ? '#e9d5ff' : '#581c87');

        // Google Maps component tokens
        s.setProperty('--gmpx-color-surface', surface);
        s.setProperty('--gmpx-color-on-surface', onSurface);
        s.setProperty('--gmpx-color-on-surface-variant', onSurfaceVariant);
        s.setProperty('--gmpx-color-outline', outline);
        s.setProperty('--gmpx-color-primary', '#a855f7');

        // Force background and text colors directly
        s.setProperty('background-color', surface, 'important');
        s.setProperty('color', onSurface, 'important');

        // Google's <gmp-place-autocomplete> shadow DOM reads
        // `prefers-color-scheme` directly and themes the dropdown dark
        // when the OS is dark, regardless of our outer CSS variables.
        // When the host page is light, pin `color-scheme: light` on the
        // element itself and inject a stylesheet into the shadowRoot so
        // the popup items render light too.
        if (!isDark) {
          s.setProperty('color-scheme', 'light', 'important');
          const injectShadowStyles = () => {
            const shadowRoot = (autocompleteElement as any).shadowRoot as ShadowRoot | null;
            if (!shadowRoot) return false;
            if (shadowRoot.querySelector('style[data-fly-light-fix]')) return true;
            const style = document.createElement('style');
            style.setAttribute('data-fly-light-fix', '');
            style.textContent = `
              :host, :host(*) {
                color-scheme: light !important;
                background-color: #ffffff !important;
                color: #1f2937 !important;
              }
              *, *::before, *::after {
                color-scheme: light !important;
              }
              [role="listbox"],
              [role="option"],
              .widget-container,
              .dropdown,
              .suggestion,
              .suggestion-item,
              [class*="suggestion"],
              [class*="dropdown"],
              [class*="listbox"] {
                background-color: #ffffff !important;
                color: #1f2937 !important;
              }
              [role="option"]:hover,
              [role="option"][aria-selected="true"] {
                background-color: #f3f4f6 !important;
              }
            `;
            shadowRoot.appendChild(style);
            return true;
          };
          setTimeout(injectShadowStyles, 100);
          setTimeout(injectShadowStyles, 500);
          setTimeout(injectShadowStyles, 1500);
        }

        // Typography scale
        s.setProperty('--gmpx-font-size', '0.95rem');
        s.setProperty('--gmpx-font-family', 'system-ui, -apple-system, sans-serif');

        // Placeholder passthrough
        if (inputElement.placeholder) {
          autocompleteElement.setAttribute('placeholder', inputElement.placeholder);
        }
        autocompleteElement.setAttribute('aria-label', inputElement.placeholder || 'Search');

        // Apply styles to the internal input element after a brief delay
        const applyInternalStyles = () => {
          const internalInput = (autocompleteElement as any).shadowRoot?.querySelector('input') ||
                                autocompleteElement.querySelector('input');
          if (internalInput) {
            (internalInput as HTMLElement).style.color = onSurface;
            (internalInput as HTMLElement).style.backgroundColor = surface;
            (internalInput as HTMLElement).style.caretColor = onSurface;
          }
        };
        setTimeout(applyInternalStyles, 100);
        setTimeout(applyInternalStyles, 500);
        setTimeout(applyInternalStyles, 1000);
      } catch {}

      // Unified handler for all event variants
      const handlePlaceEvent = async (event: any) => {
        try {
          let place = event?.place || event?.detail?.place;
          const prediction = (event as any)?.placePrediction || (event as any)?.detail?.placePrediction;
          if (!place && prediction && typeof prediction.toPlace === 'function') {
            place = await prediction.toPlace();
          }
          if (!place) return;
          // Ensure comprehensive fields are fetched for shallow locations
          if (typeof place.fetchFields === 'function') {
            await place.fetchFields({
              fields: [
                'id',
                'displayName',
                'formattedAddress',
                'addressComponents',
                'location',
                'types',
                'rating',
                'userRatingCount',
                'businessStatus',
                'websiteURI',
                'nationalPhoneNumber',
                'internationalPhoneNumber',
                'priceLevel',
                'photos',
                'reviews',
                'regularOpeningHours'
              ]
            });
          }

          const loc = place.location;
          const latVal = typeof loc?.lat === 'function' ? loc.lat() : loc?.lat;
          const lngVal = typeof loc?.lng === 'function' ? loc.lng() : loc?.lng;

          // Log the complete place object for debugging
          console.log('=== COMPLETE PLACE OBJECT FROM GOOGLE ===');
          console.log('Raw place object:', place);
          console.log('Available properties:', Object.keys(place));
          console.log('Place ID:', place.id);
          console.log('Display Name:', place.displayName);
          console.log('Formatted Address:', place.formattedAddress);
          console.log('Location:', { lat: latVal, lng: lngVal });
          console.log('Types:', place.types);
          console.log('Rating:', place.rating);
          console.log('User Rating Count:', place.userRatingCount);
          console.log('Business Status:', place.businessStatus);
          console.log('Website URI:', place.websiteURI);
          console.log('National Phone:', place.nationalPhoneNumber);
          console.log('International Phone:', place.internationalPhoneNumber);
          console.log('Address Components:', place.addressComponents);
          console.log('==========================================');

          const address_components = Array.isArray(place.addressComponents)
            ? place.addressComponents.map((c: any) => ({
                long_name: c.longText || c.long_name,
                short_name: c.shortText || c.short_name,
                types: c.types || []
              }))
            : [];

          const legacyPlace = {
            place_id: place.id || place.place_id,
            name: place.displayName || place.display_name || place.formattedAddress?.split(',')[0],
            formatted_address: place.formattedAddress,
            address_components,
            geometry: {
              location: {
                lat: () => latVal,
                lng: () => lngVal
              }
            },
            formatted_phone_number: place.nationalPhoneNumber || place.formattedPhoneNumber,
            international_phone_number: place.internationalPhoneNumber,
            website: place.websiteURI || place.website_uri,
            rating: place.rating,
            user_ratings_total: place.userRatingCount || place.user_ratings_total,
            photos: place.photos,
            types: place.types,
            business_status: place.businessStatus || place.business_status,
            opening_hours: place.regularOpeningHours || place.opening_hours,
            reviews: place.reviews,
            price_level: place.priceLevel,
            // Add coordinate shortcuts for easier access
            lat: latVal,
            lng: lngVal
          };

          console.log('=== LEGACY PLACE OBJECT SENT TO CALLBACK ===');
          console.log('Legacy place object:', legacyPlace);
          console.log('Legacy place name:', legacyPlace.name);
          console.log('Legacy place formatted_address:', legacyPlace.formatted_address);
          console.log('============================================');
          onPlaceSelect(legacyPlace);

          // Mirror display value into original input if it remains enabled
          try {
            if (inputElement && (inputElement as any).disabled !== true) {
              inputElement.value = legacyPlace.name || legacyPlace.formatted_address || '';
            }
          } catch {}
        } catch (err) {
          console.error('Error handling place selection:', err);
        }
      };

      autocompleteElement.addEventListener('gmp-placeselect', handlePlaceEvent as any);
      autocompleteElement.addEventListener('gmp-select', handlePlaceEvent as any);
      autocompleteElement.addEventListener('gmp-placechange', handlePlaceEvent as any);

      return autocompleteElement;
    } else if (window.google.maps.places.Autocomplete) {
      console.log('Falling back to classic Autocomplete API');
      
      // Use classic Autocomplete as fallback
      const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
        types: ['establishment'],
        fields: ['place_id', 'name', 'formatted_address', 'geometry', 'formatted_phone_number', 'website', 'rating', 'user_ratings_total', 'photos', 'types', 'business_status', 'opening_hours', 'reviews']
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        console.log('Place selected (classic API):', place);
        if (place && place.geometry?.location) {
          onPlaceSelect(place);
        }
      });

      // Add some styling to make it clear the autocomplete is active
      inputElement.setAttribute('autocomplete', 'off');
      const isDarkFallback = document.documentElement.classList.contains('dark');
      inputElement.style.backgroundColor = isDarkFallback ? '#1c1917' : '#fff';
      inputElement.style.color = isDarkFallback ? '#e7e5e4' : '#1f2937';
      
      // Add pac-container style fix (dark/light mode aware)
      setTimeout(() => {
        const pacContainers = document.querySelectorAll('.pac-container');
        pacContainers.forEach(container => {
          (container as HTMLElement).style.zIndex = '9999';
          if (isDarkFallback) {
            (container as HTMLElement).style.backgroundColor = '#1c1917';
            (container as HTMLElement).style.color = '#e7e5e4';
            (container as HTMLElement).style.borderColor = '#57534e';
          }
        });
      }, 500);

      return autocomplete;
    } else {
      throw new Error('No Autocomplete API available');
    }
  } catch (error) {
    console.error('Error creating autocomplete:', error);
    throw error;
  }
}